// Recebível projetado por UC — ponte entre o cálculo puro e as telas.
//
// A regra em si vive em `supabase/functions/_shared/recebivelUc.ts` e é
// REEXPORTADA, não reimplementada, pelo mesmo motivo de `splitParceira.ts`:
// isto é dinheiro, e duas cópias divergiriam na primeira mudança de deságio.
// Aqui fica só o que depende de rede: buscar faturas, casar UC e montar a
// agenda de cada uma.
//
//   node --test src/lib/__tests__/recebiveisUc.test.ts

import {
  montarAgendaRecebiveis,
  ordinalDaData,
  piorConfianca,
  projecaoSaldoCancelada,
  rotuloDeOrdinal,
  valorSemEquatorial,
  RETENCAO_MENSAL_CANCELADA,
  type AgendaRecebiveis,
  type ConfiancaRecebivel,
  type FaturaRecebivel,
  type OrigemRecebivel,
  type RecebivelMes,
} from './recebivelUc.ts';
import { calcularProjecaoConsumo, type ConfiancaProjecao } from './projecaoConsumo.ts';
import {
  derivarStatusBoleto,
  STATUS_SEM_COBRANCA,
  type DadosStatusBoleto,
  type StatusBoleto,
} from './statusBoleto.ts';

export {
  calcularFatorAbatimento,
  calcularValorRecebivel,
  dataDoVencimento,
  faturasDoCiclo,
  inferirVencimento,
  montarAgendaRecebiveis,
  ordinalDaData,
  parteDaVigor,
  piorConfianca,
  porCompetencia,
  projecaoSaldoCancelada,
  resolverDescontoVigente,
  resolverTarifaVigente,
  rotuloDeOrdinal,
  temConteudo,
  temFaturamentoComprovado,
  valorSemEquatorial,
  vencimentoEfetivo,
  MESES_MAXIMOS_SEM_COBRANCA_CANCELADA,
  RETENCAO_MENSAL_CANCELADA,
  ADERENCIA_VENCIMENTO_ALTA,
  DESCONTO_PADRAO_BANDEIRA,
  DESCONTO_PADRAO_TARIFA,
  FATOR_ABATIMENTO_PADRAO,
  LAG_VENCIMENTO_PADRAO,
  LIMITE_FATOR_ABATIMENTO,
  MAX_MESES_TARIFA,
  MESES_FATOR_ABATIMENTO,
  MESES_MINIMOS_VENCIMENTO,
  COMPETENCIAS_MINIMAS_FATURADAS,
  type AgendaRecebiveis,
  type ConfiancaRecebivel,
  type DescontoVigente,
  type FatorAbatimento,
  type FaturaRecebivel,
  type OrigemRecebivel,
  type RecebivelMes,
  type TarifaVigente,
  type ValorRecebivel,
  type VencimentoInferido,
} from './recebivelUc.ts';

/** Colunas de `faturas` que a agenda precisa. Exportada para a query não divergir do tipo. */
export const COLUNAS_FATURA_RECEBIVEL =
  'uc_codigo_norm, mes_referencia, data_vencimento, vencimento_boleto, status_boleto_vigor, ' +
  'valor_boleto_vigor, valor_tarifa_distribuidora, tarifa_bandeira, desconto_tarifa, ' +
  'desconto_bandeira, qtd_injecao_scee_boleto, qtd_adc_bandeira_boleto, consumo_ativo_kwh, ambiente, ' +
  // Necessárias para `derivarStatusBoleto` — sem elas o status exibido é o
  // carimbo gravado no passado, não o de hoje. Ver `normalizarStatus`.
  'status_fatura, data_pgto_boleto, quitado_em, energia_injetada_gd1_kwh, energia_injetada_gd2_kwh, ' +
  // Boleto único: tirar a Equatorial do total — ver `valorSemEquatorial`.
  'valor_emissao_boleto_unico, valor_fatura_distribuidora, equatorial_quitada_pelo_cliente, ' +
  'debito_automatico_equatorial, empresa_parceira_id';

/**
 * Troca o status GRAVADO pelo status DERIVADO, antes de qualquer outra conta.
 *
 * `status_boleto_vigor` é um carimbo do passado, e o par Vencido/Em aberto vira
 * sozinho com o passar do tempo — uma fatura gravada como "Em aberto" em julho
 * está vencida em agosto sem ninguém ter tocado nela. `src/lib/statusBoleto.ts`
 * é a fonte única que o Painel Executivo e a Central de Inadimplência já usam
 * para chegar ao mesmo número; esta tela precisa concordar com elas, ainda mais
 * agora que há um botão decidindo a próxima ação pelo status.
 *
 * Feito no limite de entrada, uma vez: assim o filtro, o badge e a ação leem
 * todos o mesmo valor, e o módulo `_shared` continua sem depender de `src/`.
 */
export function normalizarStatus(
  faturas: readonly FaturaRecebivel[],
  hoje = new Date(),
): FaturaRecebivel[] {
  return faturas.map((f) => ({
    ...f,
    status_boleto_vigor: derivarStatusBoleto(f as DadosStatusBoleto, hoje),
  }));
}

/** A UC, no que a agenda precisa. */
export interface UcRecebivel {
  id: number;
  numero_uc?: string | null;
  numero_uc_norm?: string | null;
  outro_numero_uc_norm?: string | null;
  nome_cliente?: string | null;
  situacao?: string | null;
  desconto_tarifa?: number | null;
  desconto_bandeira?: number | null;
  empresa_parceira_id?: string | null;
  tipo_ligacao?: string | null;
  // Campos que não entram no cálculo — existem para a tela poder filtrar pelos
  // mesmos recortes de `UnidadesList` (cidade, concessionária, grupo tarifário).
  cidade?: string | null;
  estado?: string | null;
  concessionaria?: string | null;
  grupo_tarifario?: string | null;
  negociacao_id?: number | null;
}

export interface AgendaDaUc extends AgendaRecebiveis {
  ucId: number;
  numeroUc: string;
  nomeCliente: string;
  /** true quando o boleto é emitido pela parceira e só o repasse é caixa da Vigor. */
  deParceira: boolean;
  /** A UC de origem, preservada para os filtros da tela. */
  uc: UcRecebivel;
}

/** A confiança da projeção de consumo, no vocabulário do recebível. */
function traduzirConfianca(c: ConfiancaProjecao): ConfiancaRecebivel {
  return c === 'alta' ? 'alta' : c === 'media' ? 'media' : 'baixa';
}

/**
 * Monta a agenda de uma UC a partir das faturas dela.
 *
 * O consumo projetado vem de `calcularProjecaoConsumo` — a peça entregue na
 * demanda anterior, já validada. Aqui ele só é convertido em dinheiro; nada
 * de reimplementar projeção de consumo.
 */
export function montarAgendaDaUc({
  uc,
  faturas,
  percentualVigor,
  horizonteMeses = 6,
  hoje = new Date(),
}: {
  uc: UcRecebivel;
  faturas: readonly FaturaRecebivel[];
  /** `empresas_parceiras.percentual_vigor` quando a UC é de parceira. */
  percentualVigor?: number | null;
  horizonteMeses?: number;
  hoje?: Date;
}): AgendaDaUc {
  const ordinalAtual = hoje.getFullYear() * 12 + hoje.getMonth();
  // O status precisa ser o de HOJE, não o carimbado no banco — ver normalizarStatus.
  const faturasNorm = normalizarStatus(faturas, hoje);

  // A projeção de consumo lê o histórico observado das próprias faturas.
  const observacoes = faturasNorm
    .map((f) => {
      const ord = ordinalDeMesReferencia(f.mes_referencia);
      const consumoKwh = Number(f.consumo_ativo_kwh);
      return ord != null && Number.isFinite(consumoKwh) ? { ordinal: ord, consumoKwh, injecaoKwh: 0 } : null;
    })
    .filter((o): o is { ordinal: number; consumoKwh: number; injecaoKwh: number } => o != null);

  // A projeção começa na competência ANTERIOR à atual (a função começa no mês
  // seguinte ao informado, daí o −2). A fatura de AGO vence em SET em 68% dos
  // casos: se ela ainda não chegou, é recebível do mês corrente e precisa de
  // previsão. `montarAgendaRecebiveis` descarta o mês se a fatura já existe ou
  // se o vencimento previsto já passou. O `ordinalAtual` só escolhe quais meses
  // projetar — não muda a base da conta dos outros.
  const projecao = calcularProjecaoConsumo({
    observacoes,
    ordinalAtual: ordinalAtual - 2,
    horizonteMeses: horizonteMeses + 1,
    tipoLigacao: uc.tipo_ligacao,
  });

  // UC cancelada não segue o consumo: só gasta o saldo que sobrou, decaindo
  // mês a mês — ver `projecaoSaldoCancelada`.
  const cancelada = uc.situacao === 'Cancelada';
  let projecaoKwh = new Map<number, number>();
  if (cancelada) {
    projecaoKwh = projecaoSaldoCancelada(faturasNorm, ordinalAtual, horizonteMeses);
  } else {
    for (const m of projecao.projecao) {
      // A base do faturamento é o que a Vigor compensa, não o consumo bruto.
      projecaoKwh.set(m.ordinal, m.compensavelProjetadoKwh);
    }
  }

  const agenda = montarAgendaRecebiveis({
    faturas: faturasNorm,
    projecaoKwh,
    descontoTarifa: Number(uc.desconto_tarifa) || 0,
    descontoBandeira: Number(uc.desconto_bandeira) || 0,
    percentualVigor,
    competenciaAtualOrdinal: ordinalAtual,
    confiancaProjecaoConsumo: cancelada ? 'baixa' : traduzirConfianca(projecao.confianca),
  });

  const avisosCancelada = cancelada
    ? [
        projecaoKwh.size > 0
          ? `UC cancelada: a previsão é a cobrança do saldo pendente, caindo ${Math.round((1 - RETENCAO_MENSAL_CANCELADA) * 100)}% ao mês a partir da última fatura cobrada.`
          : 'UC cancelada sem saldo pendente a cobrar: só entram os boletos já emitidos.',
      ]
    : projecao.avisos;

  return {
    ...agenda,
    avisos: [...agenda.avisos, ...avisosCancelada],
    ucId: uc.id,
    numeroUc: String(uc.numero_uc ?? uc.numero_uc_norm ?? ''),
    nomeCliente: String(uc.nome_cliente ?? ''),
    deParceira: !!uc.empresa_parceira_id,
    uc,
  };
}

function ordinalDeMesReferencia(m: unknown): number | null {
  const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
  if (!m) return null;
  const [mes, ano] = String(m).toUpperCase().split('/');
  const i = MESES.indexOf(mes);
  const a = Number(ano);
  return i < 0 || !Number.isFinite(a) ? null : a * 12 + i;
}

// ─────────────────────── cadastros duplicados ───────────────────────

export interface UcsDeduplicadas<T> {
  /** Uma UC por unidade física. */
  ucs: T[];
  /** As que foram absorvidas — mesma UC física, cadastro repetido. */
  duplicadas: T[];
}

/**
 * Uma UC física por linha.
 *
 * A base tem o MESMO cliente cadastrado duas vezes, com `numero_uc` e
 * `outro_numero_uc` trocados entre os dois registros — Denis Henrique aparece
 * como id 29 (`270840801200` / `10007747096`) e id 2798 (`10007747096` /
 * `270840801200`). Os dois casam com as mesmas faturas e produzem a mesma
 * agenda, então somar os dois conta o recebível duas vezes.
 *
 * Medido em produção (28/08/2026): 208 UCs em disputa, R$ 254.690 de um total
 * projetado de R$ 5.409.854 — **4,7% do valor da carteira contado em dobro**.
 * Um agregado que infla 4,7% sem avisar é exatamente o que `INV-01` proíbe.
 *
 * Não corrige o cadastro (isso é trabalho humano) e não esconde nada: devolve
 * as absorvidas para a tela poder dizer quantas foram e quais.
 */
export function deduplicarUcs<T extends { numero_uc_norm?: string | null; outro_numero_uc_norm?: string | null }>(
  ucs: readonly T[],
): UcsDeduplicadas<T> {
  const reivindicado = new Set<string>();
  const mantidas: T[] = [];
  const duplicadas: T[] = [];

  for (const uc of ucs) {
    // `numero_uc_norm` e `outro_numero_uc_norm` são iguais em várias linhas;
    // o Set já cuida disso sem contar a UC como duplicata de si mesma.
    const codigos = [uc.numero_uc_norm, uc.outro_numero_uc_norm].filter(
      (c): c is string => !!c && c.trim() !== '',
    );
    if (codigos.length === 0) {
      // Sem código não dá para casar com fatura nenhuma; segue, e a ausência de
      // faturas já a remove adiante.
      mantidas.push(uc);
      continue;
    }
    if (codigos.some((c) => reivindicado.has(c))) {
      duplicadas.push(uc);
      continue;
    }
    for (const c of codigos) reivindicado.add(c);
    mantidas.push(uc);
  }

  return { ucs: mantidas, duplicadas };
}

// ─────────────────────── agregação por vencimento ───────────────────────

export interface TotalMensal {
  /** Ordinal do MÊS DE VENCIMENTO — é por ele que o caixa se organiza. */
  ordinal: number;
  rotulo: string;
  emitido: { valorBoleto: number; valorVigor: number; ucs: number };
  /** Projeção de UC que JÁ demonstrou faturar — é isto que responde "quanto vou receber". */
  projetado: { valorBoleto: number; valorVigor: number; ucs: number };
  /**
   * Projeção de UC que ainda não faturou (1º ciclo, sem injeção).
   *
   * Fica FORA de `projetado` de propósito. Medido em 29/08/2026: eram
   * R$ 302.254 em setembro, 47% do projetado, vindos de clientes que nunca
   * foram cobrados. Somar isso num "quanto vou receber" responde a pergunta
   * errada — é carteira futura, não caixa do mês. Visível como potencial,
   * nunca no total.
   */
  emEntrada: { valorBoleto: number; valorVigor: number; ucs: number };
  /**
   * UCs cuja projeção entrou frágil NESTE balde.
   *
   * Mede `confiancaValor` e `confiancaMes`, nunca o dia: o dia não muda quanto
   * entra nem em que mês entra. Contar o dia aqui marcaria como frágil quase
   * toda a carteira (o dia acerta em 33%, o mês em 90%) e o número deixaria de
   * distinguir qualquer coisa.
   */
  ucsBaixaConfianca: number;
}

/**
 * Soma as agendas por MÊS DE VENCIMENTO — o eixo que a demanda pede e que o
 * Painel Executivo consome.
 *
 * Agrupa por `ordinalVencimento`, não por competência: em 68% das faturas o
 * dinheiro cai no mês seguinte ao consumo, então somar por competência
 * descreveria um caixa que não existe.
 *
 * Emitido e projetado NUNCA se somam num total só: um é fato, o outro é
 * estimativa, e a diferença é a única coisa que impede o Financeiro de tratar
 * previsão como extrato.
 */
export function agregarPorVencimento(agendas: readonly AgendaDaUc[]): TotalMensal[] {
  const mapa = new Map<number, TotalMensal>();

  const linha = (ord: number): TotalMensal => {
    let l = mapa.get(ord);
    if (!l) {
      l = {
        ordinal: ord,
        rotulo: rotuloDeOrdinal(ord),
        emitido: { valorBoleto: 0, valorVigor: 0, ucs: 0 },
        projetado: { valorBoleto: 0, valorVigor: 0, ucs: 0 },
        emEntrada: { valorBoleto: 0, valorVigor: 0, ucs: 0 },
        ucsBaixaConfianca: 0,
      };
      mapa.set(ord, l);
    }
    return l;
  };

  for (const agenda of agendas) {
    for (const m of agenda.meses) {
      if (m.ordinalVencimento == null) continue;
      const l = linha(m.ordinalVencimento);
      // Emitido é fato e conta sempre — o boleto existe, faturamento
      // comprovado ou não. Projeção de UC que nunca faturou vai para o balde
      // separado, fora do total que responde "quanto vou receber".
      const alvo =
        m.origem === 'emitido' ? l.emitido : m.faturamentoComprovado ? l.projetado : l.emEntrada;
      alvo.valorBoleto = arredondar(alvo.valorBoleto + m.valorBoleto);
      alvo.valorVigor = arredondar(alvo.valorVigor + m.valorVigor);
      alvo.ucs += 1;
      if (
        m.origem === 'projetado' &&
        m.faturamentoComprovado &&
        piorConfianca(m.confiancaValor, m.confiancaMes) === 'baixa'
      ) {
        l.ucsBaixaConfianca += 1;
      }
    }
  }

  return [...mapa.values()].sort((a, b) => a.ordinal - b.ordinal);
}

const arredondar = (v: number) => Math.round(v * 100) / 100;

/** Uma linha por mês por UC, achatada — o formato que a tabela de conferência exibe. */
export interface LinhaConferencia extends RecebivelMes {
  ucId: number;
  numeroUc: string;
  nomeCliente: string;
  deParceira: boolean;
  avisosDaUc: string[];
  /** A UC de origem, para os filtros de cadastro (cidade, concessionária...). */
  uc: UcRecebivel;
}

export function achatarParaConferencia(agendas: readonly AgendaDaUc[]): LinhaConferencia[] {
  const linhas: LinhaConferencia[] = [];
  for (const a of agendas) {
    for (const m of a.meses) {
      linhas.push({
        ...m,
        ucId: a.ucId,
        numeroUc: a.numeroUc,
        nomeCliente: a.nomeCliente,
        deParceira: a.deParceira,
        avisosDaUc: a.avisos,
        uc: a.uc,
      });
    }
  }
  return linhas.sort(
    (a, b) => (a.ordinalVencimento ?? a.ordinal) - (b.ordinalVencimento ?? b.ordinal) || b.valorBoleto - a.valorBoleto,
  );
}

// ─────────────────────────── filtros ───────────────────────────

/** Valor sentinela para "o campo está vazio no cadastro" — vazio é um recorte útil. */
export const NAO_INFORMADO = '__nao_informado__';
/** Valor sentinela do filtro de faturador para "UC da própria Vigor". */
export const FATURADOR_VIGOR = '__vigor__';

export interface FiltrosRecebiveis {
  /** Casa com número da UC ou nome do cliente. */
  busca: string;
  /** Ordinal do mês de VENCIMENTO. */
  mesVencimento: number | null;
  origem: '' | OrigemRecebivel;
  /** Status do boleto já emitido — 'Pago', 'Em aberto', 'Vencido'... */
  statusBoleto: string;
  confiancaValor: '' | ConfiancaRecebivel;
  /** 'certo' = mês firme ou médio; 'incerto' = mês frágil. */
  certezaMes: '' | 'certo' | 'incerto';
  cidade: string;
  concessionaria: string;
  grupoTarifario: string;
  situacao: string;
  /** id da empresa parceira, `FATURADOR_VIGOR`, ou vazio. */
  faturador: string;
  valorMin: string;
  valorMax: string;
  /** Só linhas de UC que tem alguma ressalva. */
  somenteComRessalva: boolean;
  /** Só linhas cujo vencimento já passou e ainda não têm boleto. */
  somenteAtrasadas: boolean;
}

export const FILTROS_VAZIOS: FiltrosRecebiveis = {
  busca: '',
  mesVencimento: null,
  origem: '',
  statusBoleto: '',
  confiancaValor: '',
  certezaMes: '',
  cidade: '',
  concessionaria: '',
  grupoTarifario: '',
  situacao: '',
  faturador: '',
  valorMin: '',
  valorMax: '',
  somenteComRessalva: false,
  somenteAtrasadas: false,
};

/**
 * Quantos filtros estão ativos — alimenta o contador no botão "Filtros",
 * mesmo padrão de `UnidadesList`.
 *
 * A busca NÃO conta: ela tem campo próprio e visível, e contá-la faria o
 * badge piscar a cada tecla.
 */
export function contarFiltrosAtivos(f: FiltrosRecebiveis): number {
  return [
    f.mesVencimento != null,
    !!f.origem,
    !!f.statusBoleto,
    !!f.confiancaValor,
    !!f.certezaMes,
    !!f.cidade,
    !!f.concessionaria,
    !!f.grupoTarifario,
    !!f.situacao,
    !!f.faturador,
    !!f.valorMin,
    !!f.valorMax,
    f.somenteComRessalva,
    f.somenteAtrasadas,
  ].filter(Boolean).length;
}

/**
 * Folga usada quando a UC não tem janela de dias conhecida.
 *
 * O erro do vencimento estimado tem p90 de 9 dias (medido em 912 casos), então
 * abaixo disso o "atraso" pode ser só a imprecisão da própria estimativa.
 */
export const FOLGA_ATRASO_DIAS = 9;

/** Último dia plausível do vencimento daquela competência. */
function limiteDoVencimento(l: LinhaConferencia): string | null {
  if (!l.vencimento) return null;
  const ano = Number(l.vencimento.slice(0, 4));
  const mes = Number(l.vencimento.slice(5, 7));
  const ultimoDoMes = new Date(Date.UTC(ano, mes, 0)).getUTCDate();

  if (l.janelaDias) {
    const dia = Math.min(l.janelaDias.max, ultimoDoMes);
    return `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
  }
  const d = new Date(`${l.vencimento}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + FOLGA_ATRASO_DIAS);
  return d.toISOString().slice(0, 10);
}

/**
 * Uma linha está ATRASADA quando **até o último dia plausível** já passou e
 * ainda não existe boleto.
 *
 * É o estado que só esta tela enxerga: a fatura da distribuidora deveria ter
 * chegado, o cliente deveria estar sendo cobrado, e não está. Não é
 * inadimplência — não há boleto para vencer — é fatura que não entrou.
 *
 * O limite é o FIM da janela observada, não o dia estimado, porque a estimativa
 * erra 2 dias na mediana e o atraso mediano dos candidatos é de 3 dias: alarmar
 * no dia seguinte é alarmar dentro da própria margem de erro. Medido na carteira
 * inteira, o critério do dia exato acusava 25 linhas e o do fim da janela acusa
 * 9 — as outras 16 eram ruído da inferência, não fatura faltando.
 */
export function estaAtrasada(l: LinhaConferencia, hojeIso: string): boolean {
  if (l.origem !== 'projetado' || !l.vencimento) return false;
  const limite = limiteDoVencimento(l);
  return !!limite && limite < hojeIso;
}

const texto = (v: unknown) => String(v ?? '').trim().toLowerCase();

/** Aplica todos os filtros. Puro — testável sem React. */
export function filtrarLinhas(
  linhas: readonly LinhaConferencia[],
  f: FiltrosRecebiveis,
  hojeIso: string,
): LinhaConferencia[] {
  const q = texto(f.busca);
  const min = f.valorMin === '' ? null : Number(f.valorMin);
  const max = f.valorMax === '' ? null : Number(f.valorMax);

  return linhas.filter((l) => {
    if (q && !texto(l.numeroUc).includes(q) && !texto(l.nomeCliente).includes(q)) return false;
    if (f.mesVencimento != null && l.ordinalVencimento !== f.mesVencimento) return false;
    if (f.origem && l.origem !== f.origem) return false;
    if (f.statusBoleto && (l.statusBoleto ?? '') !== f.statusBoleto) return false;
    if (f.confiancaValor && l.confiancaValor !== f.confiancaValor) return false;

    if (f.certezaMes === 'certo' && l.confiancaMes === 'baixa') return false;
    if (f.certezaMes === 'incerto' && l.confiancaMes !== 'baixa') return false;

    if (f.cidade) {
      const cidade = l.uc.cidade ? `${l.uc.cidade}/${l.uc.estado ?? ''}` : NAO_INFORMADO;
      if (cidade !== f.cidade) return false;
    }
    if (f.concessionaria && (l.uc.concessionaria || NAO_INFORMADO) !== f.concessionaria) return false;
    if (f.grupoTarifario && (l.uc.grupo_tarifario || NAO_INFORMADO) !== f.grupoTarifario) return false;
    if (f.situacao && (l.uc.situacao || NAO_INFORMADO) !== f.situacao) return false;

    if (f.faturador === FATURADOR_VIGOR && l.uc.empresa_parceira_id) return false;
    if (f.faturador && f.faturador !== FATURADOR_VIGOR && l.uc.empresa_parceira_id !== f.faturador) return false;

    // A faixa filtra pelo VALOR DO BOLETO — é o número que o operador confere.
    if (min != null && Number.isFinite(min) && l.valorBoleto < min) return false;
    if (max != null && Number.isFinite(max) && l.valorBoleto > max) return false;

    if (f.somenteComRessalva && l.avisosDaUc.length === 0) return false;
    if (f.somenteAtrasadas && !estaAtrasada(l, hojeIso)) return false;

    return true;
  });
}

// ─────────────────────── taxa real de recebimento ───────────────────────

/**
 * Meses recentes demais para julgar: ainda estão sendo pagos.
 *
 * Medido em produção: 97,4% do que é pago entra até 30 dias do vencimento, e a
 * mediana é o próprio dia do vencimento. Dois meses de folga dá margem sobrada.
 */
export const MESES_ATE_MATURAR = 2;

/** Supabase-js devolve coluna `numeric` como string — nunca confie no tipo declarado. */
const num = (v: unknown): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export interface TaxaRecebimento {
  /** Fração do emitido que historicamente entra. 0,782 = 78,2%. */
  taxa: number;
  /** Meses maduros que entraram na conta. */
  meses: number;
  emitido: number;
  recebido: number;
  /** `false` quando não houve histórico suficiente e caiu no padrão. */
  medida: boolean;
}

/**
 * Fração do emitido que a Vigor de fato recebe, medida no próprio histórico.
 *
 * Existe porque o Painel Executivo usava percentuais fixos de cenário — 8%,
 * 15% e 20% de inadimplência — e a realidade medida sobre R$ 3,6 milhões já
 * vencidos há mais de 60 dias é **21,8%**: pior que o pior cenário. Um número
 * de cenário inventado não sobrevive ao contato com a régua.
 *
 * Só entram meses já maduros: um mês que venceu semana passada ainda está
 * sendo pago e puxaria a taxa para baixo sem significar inadimplência.
 *
 * Aqueles 21,8% ainda estavam inflados por boleto cancelado e fatura-mãe de
 * parcelamento contados como calote — ver `STATUS_FORA_DA_TAXA`, que o chamador
 * aplica via `entradaDaTaxa`.
 */
export function calcularTaxaRecebimento(
  faturas: readonly { vencimento?: string | null; valor: number | string | null; pago: boolean; cobranca: boolean }[],
  hojeIso: string,
): TaxaRecebimento {
  const corte = new Date(`${hojeIso}T00:00:00Z`);
  corte.setUTCMonth(corte.getUTCMonth() - MESES_ATE_MATURAR);
  const limite = corte.toISOString().slice(0, 10);

  const meses = new Set<string>();
  let emitido = 0;
  let recebido = 0;
  for (const f of faturas) {
    if (!f.cobranca || !f.vencimento || f.vencimento >= limite) continue;
    meses.add(f.vencimento.slice(0, 7));
    // BUG corrigido: `valor` numeric do Supabase-js chega como STRING em
    // runtime apesar do tipo dizer number (mesmo defeito já coberto em
    // `recebivelUc.ts`). `emitido += f.valor` sem isto vira concatenação de
    // texto assim que a primeira fatura entra como string — a taxa sai errada
    // por uma ordem de grandeza, sem lançar nada.
    const valor = num(f.valor);
    emitido += valor;
    if (f.pago) recebido += valor;
  }

  if (emitido <= 0) {
    return { taxa: TAXA_RECEBIMENTO_PADRAO, meses: meses.size, emitido: 0, recebido: 0, medida: false };
  }
  return { taxa: recebido / emitido, meses: meses.size, emitido, recebido, medida: true };
}

/** Usado só quando não há histórico maduro. É a taxa medida em 15/09/2026, já sem cancelado/parcelado. */
export const TAXA_RECEBIMENTO_PADRAO = 0.844;

/**
 * Status que ficam FORA da taxa de recebimento, além dos sem cobrança.
 *
 * Os dois contavam como "emitido e não recebido" e inflavam a inadimplência
 * (21,4% contra ~15,6% real, medido em 15/09/2026 sobre os meses maduros):
 *
 *  - `Parcelado` é a fatura-mãe de um acordo (`CriarParcelamentoModal`). A
 *    dívida vira parcelas avulsas, que são faturas próprias e entram na taxa
 *    pelo que acontecer com elas. Contar a mãe também é contar a dívida duas
 *    vezes — e o acordo pago aparecia como calote. R$ 301 mil na janela.
 *  - `Cancelado` é boleto que deixou de existir: reemissão duplicada, cobrança
 *    errada, UC encerrada. O "a faturar" da projeção já não soma boleto
 *    cancelado (`faturasDoCiclo`), então a taxa que multiplica esse número
 *    também não pode. R$ 94 mil na janela.
 *
 * Parcela de acordo que não é paga continua `Vencido` e entra normalmente.
 */
export const STATUS_FORA_DA_TAXA: readonly StatusBoleto[] = ['Cancelado', 'Parcelado'];

/** Converte uma fatura na entrada de `calcularTaxaRecebimento`. Puro — testável sem banco. */
export function entradaDaTaxa(
  f: FaturaRecebivel,
  hoje = new Date(),
): { vencimento: string | null; valor: number; pago: boolean; cobranca: boolean } {
  const st = derivarStatusBoleto(f as DadosStatusBoleto, hoje);
  return {
    vencimento: String(f.vencimento_boleto || f.data_vencimento || '').slice(0, 10) || null,
    // Sem a conta da Equatorial: a taxa multiplica um "a faturar" que também não
    // tem ela, e um boleto único pago não pode pesar como receita da Vigor.
    valor: valorSemEquatorial(f, !!f.empresa_parceira_id),
    pago: st === 'Pago' || st === 'Quitado',
    cobranca: !STATUS_SEM_COBRANCA.includes(st) && !STATUS_FORA_DA_TAXA.includes(st),
  };
}

/**
 * Abaixo desta fração das UCs do mês típico, o mês é cobertura parcial.
 *
 * Metade é folga generosa: um mês normal da carteira tem ~700 UCs, e os meses
 * na ponta do horizonte caem para dezenas ou unidades.
 */
export const COBERTURA_MINIMA = 0.5;

export interface CoberturaDoMes {
  /** UCs que aparecem neste mês. */
  ucs: number;
  /** UCs do mês típico da carteira. */
  ucsTipico: number;
  /** `false` = o mês está na ponta do horizonte e o número é parcial. */
  completa: boolean;
}

/**
 * O mês tem gente suficiente para o número dele ser uma resposta?
 *
 * Nos meses da ponta do horizonte a projeção cobre só um punhado de UCs —
 * ABR/2027 chega com 1 UC de ~700. "A receber em ABR/2027: R$ 485" é
 * literalmente verdade e completamente enganoso: não é que a Vigor receberá
 * R$ 485, é que só se enxerga R$ 485. Sem esta marca, um mês da ponta parece
 * colapso de faturamento.
 */
export function calcularCoberturaDoMes(
  mes: Pick<TotalMensal, 'emitido' | 'projetado'>,
  todosOsMeses: readonly Pick<TotalMensal, 'emitido' | 'projetado'>[],
): CoberturaDoMes {
  const ucsDe = (m: Pick<TotalMensal, 'emitido' | 'projetado'>) => m.emitido.ucs + m.projetado.ucs;
  const contagens = todosOsMeses.map(ucsDe).filter((n) => n > 0).sort((a, b) => a - b);
  const ucsTipico = contagens.length
    ? contagens[Math.floor(contagens.length / 2)]
    : 0;
  const ucs = ucsDe(mes);
  return {
    ucs,
    ucsTipico,
    completa: ucsTipico === 0 || ucs >= ucsTipico * COBERTURA_MINIMA,
  };
}

export interface RecebimentoDoMes {
  /** Já emitido + previsto de UC que comprovadamente fatura. Antes da inadimplência. */
  aFaturar: number;
  /** O número que responde a pergunta do CEO. */
  aReceber: number;
  /** Fração que historicamente não entra — o que separa os dois acima. */
  inadimplencia: number;
  /** Fora dos totais: UC que ainda não faturou. Carteira futura, não caixa. */
  emEntrada: number;
}

/**
 * A conta final que o CEO lê, isolada do componente.
 *
 * Vive aqui e não dentro do JSX porque é a única linha do sistema que produz
 * o número da diretoria: trocar `valorBoleto` por `valorVigor` (nomes
 * parecidos, semânticas diferentes) mudaria o número em silêncio e nenhum
 * teste pegaria — o projeto não tem testes de componente.
 *
 * Usa `valorVigor`: a pergunta é quanto entra na Vigor. Até 15/09/2026 usava
 * `valorBoleto`, e o boleto único da Gran entrava com 100% — ou com a conta da
 * Equatorial dentro. Decisão do dono do produto: de UC de parceira conta só
 * `(boleto − Equatorial) × percentual_vigor`. Em UC da Vigor os dois valores são
 * iguais, então nada muda para ela.
 */
export function calcularRecebimentoDoMes(
  mes: Pick<TotalMensal, 'emitido' | 'projetado' | 'emEntrada'>,
  taxa: TaxaRecebimento,
): RecebimentoDoMes {
  const aFaturar = arredondar(mes.emitido.valorVigor + mes.projetado.valorVigor);
  return {
    aFaturar,
    aReceber: arredondar(aFaturar * taxa.taxa),
    inadimplencia: 1 - taxa.taxa,
    emEntrada: mes.emEntrada.valorVigor,
  };
}

// ─────────────────────── próxima ação ───────────────────────

export type TipoAcao = 'cobrar' | 'fatura_nao_chegou' | 'conferir_fatura' | 'corrigir_cadastro';

export interface AcaoRecebivel {
  tipo: TipoAcao;
  /** Texto do botão — um verbo, o que a pessoa vai fazer. */
  rotulo: string;
  /** Por que esta ação e não outra. Vai no tooltip. */
  motivo: string;
  /** true quando é dinheiro parado ou cliente sem cobrança — pinta o botão. */
  urgente: boolean;
}

/**
 * O que fazer com esta linha, agora. `null` = **nada a fazer**.
 *
 * A ordem é de urgência, não de frequência: uma linha pode se encaixar em
 * várias, e ganha a que custa mais dinheiro deixar parada.
 *
 * `'fatura_nao_chegou'` é o caso que só esta tela enxerga. Não é
 * inadimplência — não existe boleto para vencer. É a conta da distribuidora
 * que não entrou, então ninguém está cobrando esse cliente e ninguém sabe
 * disso. A Central de Inadimplência nunca vai mostrá-lo, porque ela parte de
 * boletos emitidos.
 *
 * NÃO existe ação genérica de "abrir a UC". Ela existiu e foi removida: na
 * carteira inteira, 4.006 das 4.369 linhas (91,7%) caíam nela, e uma coluna em
 * que quase toda linha tem botão deixa de sinalizar o que precisa de atenção —
 * o mesmo erro que a confiança única cometia. Só 8,6% das linhas têm de fato
 * algo a fazer, e são essas que ganham botão. Navegar até a UC continua
 * possível pelo número dela na primeira coluna, que é link.
 */
export function decidirAcao(l: LinhaConferencia, hojeIso: string): AcaoRecebivel | null {
  // 1. Boleto emitido e vencido: dinheiro parado, cobrança é o fluxo pronto.
  if (l.origem === 'emitido' && l.statusBoleto === 'Vencido') {
    return {
      tipo: 'cobrar',
      rotulo: 'Cobrar',
      motivo: `Boleto de ${l.competencia} venceu em ${l.vencimento?.split('-').reverse().join('/')} e não foi pago.`,
      urgente: true,
    };
  }

  // 2. Deveria ter boleto e não tem. O mais grave: não há cobrança nenhuma.
  if (estaAtrasada(l, hojeIso)) {
    return {
      tipo: 'fatura_nao_chegou',
      rotulo: 'Ver por quê',
      motivo:
        `A fatura de ${l.competencia} deveria ter vencido por volta de ` +
        `${l.vencimento?.split('-').reverse().join('/')} e não existe boleto. ` +
        'Ou a conta da distribuidora não entrou, ou não foi processada — e ninguém está cobrando este cliente.',
      urgente: true,
    };
  }

  // 3. Fatura que não fecha com a própria energia e tarifa.
  if (l.avisosDaUc.some((a) => a.startsWith('Fatura a conferir'))) {
    return {
      tipo: 'conferir_fatura',
      rotulo: 'Conferir fatura',
      motivo: l.avisosDaUc.find((a) => a.startsWith('Fatura a conferir')) ?? '',
      urgente: false,
    };
  }

  // 4. O cadastro é o que está impedindo uma boa estimativa.
  const cadastro = l.avisosDaUc.find(
    (a) => a.includes('não tem desconto no cadastro') || a.includes('está defasado'),
  );
  if (cadastro) {
    return { tipo: 'corrigir_cadastro', rotulo: 'Corrigir cadastro', motivo: cadastro, urgente: false };
  }

  // Linha saudável: em dia, ou previsão futura sem ressalva. Nada a fazer.
  return null;
}

/** Opções de um filtro, extraídas dos próprios dados e já ordenadas. */
export function opcoesDe(
  linhas: readonly LinhaConferencia[],
  campo: (l: LinhaConferencia) => string | null | undefined,
): string[] {
  const vistos = new Set<string>();
  let temVazio = false;
  for (const l of linhas) {
    const v = campo(l);
    if (v == null || String(v).trim() === '') temVazio = true;
    else vistos.add(String(v));
  }
  const lista = [...vistos].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  // "Não informado" no fim: é um recorte legítimo (é assim que se acha cadastro
  // incompleto), mas não deve competir com os valores reais no topo da lista.
  if (temVazio) lista.push(NAO_INFORMADO);
  return lista;
}
